import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAdminApi, errMsg, useSite } from "@/lib/site";
import { AdminTitle } from "./AdminMessages";

export const Field = ({ label, children }) => (
  <label className="block"><span className="block text-xs font-semibold text-slate-500 mb-1.5">{label}</span>{children}</label>
);
export const inputCls = "w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-blue-500";

const BLANK = { name: "New package", tagline: "", summary: "", description: "", features: [], price: 1000, note: "", image: "", active: false, order: 99 };

const PackageEditor = ({ pkg, onSaved, onDeleted }) => {
  const call = useAdminApi();
  const [f, setF] = useState({ ...pkg, featuresText: (pkg.features || []).join("\n") });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    const body = { ...f, price: Number(f.price), order: Number(f.order), features: f.featuresText.split("\n").map((s) => s.trim()).filter(Boolean) };
    delete body.featuresText; delete body.id;
    try { onSaved(await call("put", `/admin/packages/${pkg.id}`, body)); toast.success("Package saved"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const del = async () => {
    if (!window.confirm(`Delete "${pkg.name}"? Tip: you can hide it instead.`)) return;
    try { await call("delete", `/admin/packages/${pkg.id}`); onDeleted(pkg.id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-3xl bg-white border p-5 sm:p-6 space-y-4" data-testid={`admin-package-${pkg.id}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-display font-bold text-lg">{pkg.name}</p>
        <label className="flex items-center gap-2 text-sm">{f.active ? "Visible" : "Hidden"}<Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid={`admin-package-active-${pkg.id}`} /></label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Name"><input className={inputCls} value={f.name} onChange={set("name")} data-testid={`admin-package-name-${pkg.id}`} /></Field>
        <Field label="Price (USD)"><input type="number" min="1" className={inputCls} value={f.price} onChange={set("price")} data-testid={`admin-package-price-${pkg.id}`} /></Field>
        <Field label="Order"><input type="number" className={inputCls} value={f.order} onChange={set("order")} data-testid={`admin-package-order-${pkg.id}`} /></Field>
      </div>
      <Field label="Tagline"><input className={inputCls} value={f.tagline} onChange={set("tagline")} data-testid={`admin-package-tagline-${pkg.id}`} /></Field>
      <Field label="Short summary"><textarea rows={2} className={inputCls} value={f.summary} onChange={set("summary")} data-testid={`admin-package-summary-${pkg.id}`} /></Field>
      <Field label="Full description (service page)"><textarea rows={3} className={inputCls} value={f.description} onChange={set("description")} data-testid={`admin-package-description-${pkg.id}`} /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="What's included (one per line)"><textarea rows={5} className={inputCls} value={f.featuresText} onChange={set("featuresText")} data-testid={`admin-package-features-${pkg.id}`} /></Field>
        <div className="space-y-3">
          <Field label="Image URL"><input className={inputCls} value={f.image} onChange={set("image")} data-testid={`admin-package-image-${pkg.id}`} /></Field>
          <Field label="Small print"><input className={inputCls} value={f.note} onChange={set("note")} data-testid={`admin-package-note-${pkg.id}`} /></Field>
        </div>
      </div>
      <div className="flex justify-between">
        <button onClick={del} className="flex items-center gap-1.5 text-sm text-red-600 hover:underline" data-testid={`admin-package-delete-${pkg.id}`}><Trash2 className="w-4 h-4" /> Delete</button>
        <button onClick={save} disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid={`admin-package-save-btn-${pkg.id}`}>{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
};

export default function AdminPackages() {
  const call = useAdminApi();
  const { reload } = useSite();
  const [list, setList] = useState(null);
  useEffect(() => { call("get", "/admin/packages").then(setList).catch((e) => toast.error(errMsg(e))); }, [call]);
  const add = async () => {
    try { const p = await call("post", "/admin/packages", BLANK); setList([...list, p]); toast.success("Package added (hidden until you switch it on)"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-packages">
      <div className="flex items-start justify-between gap-3"><AdminTitle sub="Services, prices and what's included">Packages</AdminTitle>
        <button onClick={add} className="flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-semibold" data-testid="admin-package-add-btn"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="space-y-4">
        {(list || []).map((p) => (
          <PackageEditor key={p.id} pkg={p}
            onSaved={(np) => { setList(list.map((x) => x.id === np.id ? np : x)); reload(); }}
            onDeleted={(id) => { setList(list.filter((x) => x.id !== id)); reload(); }} />
        ))}
      </div>
    </div>
  );
}
